// Shared Clone-target modal (PR #135, 2026-05-22). Mirrors commonsku's
// "Copy Quote" / "Copy Estimate" / "Copy Presentation" popup —
// rep picks a target client + types a new project name + picks which
// items/lines to copy (all or subset). Used by both
// PresentationDetailPage and OrderDetailPage Clone buttons.
//
// Submit shape (consumer-controlled — modal is presentation-only):
//   { clientId, projectName, selectedIds?: string[] }
// Consumer wraps the chosen values into the relevant endpoint's body
// (e.g. POST /presentations/:id/clone with `new_project: {client_id,
// name}` + `selected_item_ids: [...]`).

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export interface CloneSourceItem {
  /** Stable id for the item/line (presentation_item.id or
   *  line_item.id). */
  id: string;
  /** Human-readable label shown in the All-Products picker (typically
   *  the product name + color/SKU). */
  label: string;
}

export interface CloneTargetSubmit {
  clientId: string;
  projectName: string;
  /** When undefined, "All Products" was selected (consumer omits the
   *  `selected_item_ids` body field). When set, only these ids
   *  should be cloned. */
  selectedIds?: string[];
}

interface ClientListRow {
  id: string;
  name: string;
}

interface ClientListResponse {
  rows: ClientListRow[];
  next_cursor?: { id: string; created_at: string } | null;
}

export function CloneTargetModal(props: {
  /** Modal title. e.g. "Copy Quote", "Copy Presentation". */
  title: string;
  /** Pre-filled default for the project name field. */
  defaultProjectName: string;
  /** Initial client to suggest (the source's client). The picker lets
   *  the rep change to any client. */
  defaultClient: { id: string; name: string };
  /** Source items/lines — used to render the All-Products picker. */
  sourceItems: CloneSourceItem[];
  submitting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onSubmit: (next: CloneTargetSubmit) => void;
}) {
  const {
    title,
    defaultProjectName,
    defaultClient,
    sourceItems,
    submitting,
    errorMessage,
    onCancel,
    onSubmit,
  } = props;

  const [clientId, setClientId] = useState(defaultClient.id);
  const [clientName, setClientName] = useState(defaultClient.name);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [projectName, setProjectName] = useState(defaultProjectName);
  const [allProducts, setAllProducts] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(
    new Set(sourceItems.map((it) => it.id)),
  );

  // Close on ESC (modal is fixed overlay).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitting, onCancel]);

  // Client picker — only queries when the user opens it and has typed
  // something. Keeps the modal cheap to render.
  const trimmedSearch = clientSearch.trim();
  const clientsQ = useQuery({
    queryKey: ["clone-target", "clients", trimmedSearch],
    queryFn: () =>
      api<ClientListResponse>(
        `/clients?search=${encodeURIComponent(trimmedSearch)}&limit=20`,
      ),
    enabled: showClientPicker && trimmedSearch.length > 0,
  });

  const visibleItems = useMemo(
    () => sourceItems.filter((it) => it.label.length > 0),
    [sourceItems],
  );

  const canSubmit =
    clientId.trim().length > 0 &&
    projectName.trim().length > 0 &&
    (allProducts || selected.size > 0) &&
    !submitting;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    onSubmit({
      clientId,
      projectName: projectName.trim(),
      selectedIds: allProducts ? undefined : Array.from(selected),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="clone-target-modal-title"
    >
      <div className="w-full max-w-2xl rounded-lg border border-brown-100 bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-brown-50 px-6 py-4">
          <h2
            id="clone-target-modal-title"
            className="font-display text-2xl font-semibold text-brown-500"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-brown-300 hover:text-brown-500 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 py-5">
          {/* Client */}
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Client
            </label>
            {showClientPicker ? (
              <div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brown-300" />
                  <Input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    autoFocus
                    placeholder="Search clients…"
                    className="h-9 pl-8 text-sm"
                  />
                </div>
                {trimmedSearch.length === 0 ? (
                  <p className="mt-2 text-xs italic text-brown-300">
                    Type a client name to search.
                  </p>
                ) : clientsQ.isLoading ? (
                  <p className="mt-2 text-xs text-brown-300">Searching…</p>
                ) : clientsQ.data && clientsQ.data.rows.length === 0 ? (
                  <p className="mt-2 text-xs text-brown-300">No clients match.</p>
                ) : (
                  <ul className="mt-2 max-h-48 overflow-y-auto rounded-md border border-brown-100 bg-cream-50/40">
                    {clientsQ.data?.rows.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setClientId(c.id);
                            setClientName(c.name);
                            setShowClientPicker(false);
                            setClientSearch("");
                          }}
                          className={cn(
                            "block w-full px-3 py-1.5 text-left text-sm",
                            c.id === clientId
                              ? "bg-peach-100 text-brown-500"
                              : "text-brown-500 hover:bg-cream-100",
                          )}
                        >
                          {c.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowClientPicker(false);
                    setClientSearch("");
                  }}
                  className="mt-2 text-xs font-semibold text-brown-300 hover:text-brown-500"
                >
                  Cancel client change
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowClientPicker(true)}
                disabled={submitting}
                className="flex w-full items-center justify-between rounded-md border border-brown-100 bg-white px-3 py-2 text-left text-sm text-brown-500 hover:border-peach-300 disabled:opacity-50"
              >
                <span>{clientName}</span>
                <span className="text-xs font-semibold text-brown-400">
                  Change
                </span>
              </button>
            )}
          </div>

          {/* Project Name */}
          <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Project Name
            </label>
            <Input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              maxLength={200}
              placeholder="New Project Name"
              className="h-9 text-sm"
              disabled={submitting}
            />
            <p className="mt-1 text-[11px] text-brown-300">
              A fresh project will be created under the chosen client with
              this name.
            </p>
          </div>

          {/* Items selector */}
          <div className="rounded-md border border-brown-100 bg-cream-50/40">
            <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm font-semibold text-brown-500">
              <input
                type="checkbox"
                checked={allProducts}
                onChange={(e) => {
                  const next = e.target.checked;
                  setAllProducts(next);
                  if (next) {
                    setSelected(new Set(visibleItems.map((it) => it.id)));
                  }
                }}
                className="h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
                disabled={submitting || visibleItems.length === 0}
              />
              All Products
              {visibleItems.length > 0 && (
                <span className="ml-auto text-xs font-normal text-brown-300">
                  {visibleItems.length} item{visibleItems.length === 1 ? "" : "s"}
                </span>
              )}
            </label>
            {!allProducts && visibleItems.length > 0 && (
              <ul className="max-h-56 overflow-y-auto border-t border-brown-50 px-3 py-2">
                {visibleItems.map((it) => {
                  const checked = selected.has(it.id);
                  return (
                    <li key={it.id}>
                      <label className="flex cursor-pointer items-center gap-2 py-1 text-sm text-brown-500">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(it.id);
                            else next.delete(it.id);
                            setSelected(next);
                          }}
                          className="h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
                          disabled={submitting}
                        />
                        <span className="truncate">{it.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {errorMessage && (
            <p className="text-sm text-rust-600">{errorMessage}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-brown-50 px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            <Check className="h-3.5 w-3.5" />{" "}
            {submitting ? "Copying…" : "Copy"}
          </Button>
        </div>
      </div>
    </div>
  );
}
